import * as React from 'react';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import { SerieInListDTO, SalesOrderDTO, DeliveryStatus, Res, PrintPreviewDTO } from 'core/api';
import * as Images from 'pos/Images';
import * as UI from 'core/ui';
import { Command, Cordova } from 'pos/common';
import setTakeOutOrderStatus from './setTakeOutOrderStatus';
import { QRReader } from 'pos/services';
import { canTransitionToAnyState, canClose } from './transitions';
import { Col, Cols, Flex, TextBoxButton } from 'core/ui';
import { SalesOrderFacade, TakeOutOrderFacade } from 'pos/facades';
import { Preconditions } from 'core/infrastructure-next';
import { Message, Modal, ModalUtils } from 'core/ui-next/modal';
import { SvgIcon } from 'core/ui-next';

const { Button } = UI;
const { Form, ComboBox, IntBox } = UI.controlsFor<State>();

export type TakeOutOrderModel = {
	salesOrder: Nullable<SalesOrderDTO>;
	printPreviews: PrintPreviewDTO[];
};


type Props = {
	existingSeries: SerieInListDTO[]
} & Modal.Actions<void>;

type State = {
	orderSerie: Nullable<SerieInListDTO>;
	orderNumber: Nullable<number>;
	takeOut: TakeOutOrderModel;
	searchMessage: string;
};

class SearchTakeOutOrderScreen extends PosScreenClassComponent<Props, State> {

	private form = React.createRef<UI.Form<State>>();
	private ref = React.createRef<HTMLDivElement>();

	protected getScreenId(): string {
		return 'SEARCH_TAKE_OUT_ORDER';
	}

	protected getTitle(): string {
		return Res.Web.SEARCH_TAKE_OUT_ORDER();
	}

	protected getImage(): string | SvgIcon {
		return Images.SearchTakeOutOrders;
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			null,
			null,
			this.state.takeOut.salesOrder !== null && canTransitionToAnyState(this.state.takeOut.salesOrder) ? { action: this.onManageOrderStatus, image: Images.EditTicketLine, title: Res.Web.SEARCH_TAKE_OUT_ORDER_MANAGE_STATUS_BTN() } : null,
			this.state.takeOut.salesOrder !== null ? { action: async () => Command.execute('printTakeOutOrder', this.state.takeOut.salesOrder!), image: Images.PrintTicket, title: Res.Web.SEARCH_TAKEOUT_ORDER_PRINT_ORDER() } : null,
			this.state.takeOut.salesOrder !== null && canClose(this.state.takeOut.salesOrder) ? { action: async () => this.closeAndRefresh(), image: Images.CloseTicket, title: Res.Web.SEARCH_TAKEOUT_ORDER_CLOSE_ORDER() } : null
		];
	}

	async closeAndRefresh(): Promise<void> {
		const closeResult = await Command.execute('closeTakeOutOrder', this.state.takeOut.salesOrder!);
		if (!closeResult.ok)
			return;

		await this.onSearch();

		const newOrder = this.state.takeOut.salesOrder;
		if (newOrder?.DeliveryStatus === DeliveryStatus.Delivering && newOrder?.DeliveryPerson === '') {
			await Command.execute('changeTakeOutOrderDeliveryPerson', newOrder!.Id, newOrder.DeliveryPerson, 'IGNORE_IF_NO_DELIVERY_PERSONS');
			await this.onSearch();
		}

	}

	readQR = async (): Promise<void> => {
		const qrResult = await QRReader.read();

		if (!qrResult.ok)
			return;

		const qrContent = qrResult.value.split('-');
		if (qrContent.length !== 2) {
			await Message.show(Res.Web.SEARCH_TAKEOUT_ORDER_INVALID_QR());
			return;
		}

		const serie = this.props.existingSeries.find(s => s.Name === qrContent[0]);
		if (!serie) {
			await Message.show(Res.Web.SEARCH_TAKEOUT_ORDER_SERIE_NOT_FOUND(qrContent[0]));
			return;
		}

		const orderNumber = parseInt(qrContent[1]);
		if (isNaN(orderNumber)) {
			await Message.show(Res.Web.SEARCH_TAKEOUT_ORDER_SALES_ORDER_NOT_FOUND(qrContent[1]));
			return;
		}

		this.setState({
			orderSerie: serie,
			orderNumber
		}, async () => {
			await this.form.current!.remountAll();
			await this.onSearch();
		});
	};

	createInitialState(props: Props): Nullable<State> {
		return {
			orderSerie: props.existingSeries.length !== 0 ? props.existingSeries[0] : null,
			orderNumber: null,
			takeOut: { salesOrder: null, printPreviews: [] },
			searchMessage: Res.Web.SEARCH_TAKE_OUT_ORDER_SEARCH_OR_SCAN()
		};
	}

	onComponentDidMount(): void {
		this.on('pos:takeout-order:operation-failed', async error => {
			await Message.show(error.msg);
		});
	}

	onComponentWillUnmount(): void { }

	onManageOrderStatus = async () => {
		const result = await setTakeOutOrderStatus(this.state.takeOut.salesOrder!);
		if (result.ok)
			await this.onSearch();
	};

	onSearch = async () => {

		const updated = await this.form.current!.updateModel();
		if (!updated)
			return;

		// Hacemos click en la pantalla para que pierda el foco el input que lo tuviera y se oculte el keypad
		ModalUtils.getParent(this.ref.current!).click();


		if (this.state.orderSerie === null || this.state.orderNumber === null) {
			await Message.show(Res.Web.SEARCH_TAKEOUT_ORDER_SERIE_OR_NUMBER_ARE_MISSING());
			this.setState({ takeOut: { salesOrder: null, printPreviews: [] } });
			return;
		}

		const result = await TakeOutOrderFacade.searchOrder(this.state.orderSerie.Name, this.state.orderNumber);
		const takeOut: TakeOutOrderModel = {
			salesOrder: result.SalesOrder,
			printPreviews: result.Previews
		};

		if (takeOut.salesOrder === null) {
			await Message.show(Res.Web.SEARCH_TAKE_OUT_ORDER_NO_ORDER());
			this.setState({ searchMessage: Res.Web.SEARCH_TAKE_OUT_ORDER_NO_ORDER() });
			return;
		}

		this.setState({ takeOut });
	};

	protected getContent(): React.ReactNode {
		const scanQRButton = Cordova.isCordovaNavigator()
			? <Button buttonStyle='info' className='m-l-sm' onClick={this.readQR}><i className='fa fa-qrcode m-r-sm m-l-sm' /></Button>
			: null;

		return <Flex.Horizontal ref={this.ref}>
			<Flex.Item size='*' className='m-t-sm'>
				<Flex.Vertical>
					<Flex.Item>
						<Form model={this.state} ref={this.form} className='m-b-n-sm'>
							<Cols>
								<Col width='85px'>
									<ComboBox linkTo={x => x.orderSerie} options={this.props.existingSeries} placeholder={Res.Web.SEARCH_TAKE_OUT_ORDER_SERIE_PLACEHOLDER()} required tabIndex={-1} />
								</Col>
								<Col width='*'>
									<IntBox
										linkTo={x => x.orderNumber}
										className='m-l-xs'
										placeholder={Res.Web.SEARCH_TAKE_OUT_ORDER_NUMBER_PLACEHOLDER()}
										onKeyDown={UI.whenEnterKey(this.onSearch)}
										allowNegative={false}
										required >
										<TextBoxButton buttonStyle='info' onClick={this.onSearch} iconClassName='fa fa-search' />
									</IntBox>
								</Col>
								<Col>
									{scanQRButton}
								</Col>
							</Cols>
						</Form>
						<TakeOutOrder salesOrder={this.state.takeOut.salesOrder} searchMessage={this.state.searchMessage} />
					</Flex.Item>
					<Flex.Item size='*'>
						{this.state.takeOut.printPreviews.length !== 0 ?
							<div className='m-sm '>
								<UI.TemplatePrintPreview preview={this.state.takeOut.printPreviews[0]} />
							</div>
							: null}
					</Flex.Item>
				</Flex.Vertical>
			</Flex.Item>
		</Flex.Horizontal >;
	}
}

function TakeOutOrder(props: { salesOrder: Nullable<SalesOrderDTO>, searchMessage: string }): React.ReactNode {
	if (props.salesOrder === null)
		return <div className='font-trans m-t-xxxl text-18 text-center p-md'>{props.searchMessage}</div>;

	const statusToStyle: { [key in DeliveryStatus]: { className: string, image: React.ReactNode }
	} = {
		[DeliveryStatus.New]: { className: 'info', image: <SvgIcon color='#61AAED' width={'24px'} height={'24px'} src={Images.TakeOutNew} /> },
		[DeliveryStatus.Accepted]: { className: 'success', image: <SvgIcon color='#75CA8D' width={'24px'} height={'24px'} src={Images.TakeOutAccepted} /> },
		[DeliveryStatus.Rejected]: { className: 'danger2', image: <SvgIcon color="#FF8A95" width={'24px'} height={'24px'} src={Images.TakeOutRejected} /> },
		[DeliveryStatus.Preparing]: { className: 'warning2', image: <SvgIcon color="#FFBA7B" width={'24px'} height={'24px'} src={Images.TakeOutPreparing} /> },
		[DeliveryStatus.Prepared]: { className: 'warning2', image: <SvgIcon color="#FFBA7B" width={'24px'} height={'24px'} src={Images.TakeOutPrepared} /> },
		[DeliveryStatus.Delivering]: { className: 'primary2', image: <SvgIcon color="#C291F3" width={'24px'} height={'24px'} src={Images.TakeOutDelivering} /> },
		[DeliveryStatus.Delivered]: { className: 'primary2', image: <SvgIcon color="#C291F3" width={'24px'} height={'24px'} src={Images.TakeOutDelivered} /> },
	};


	return <Cols className={`m-l-xs m-r-xs p-xs border panel-horizontal ${statusToStyle[props.salesOrder.DeliveryStatus].className} `} >
		<Col width='*' className='text-18 font-bold'>
			<div style={{ display: 'flex', alignItems: 'center' }}>
				<div className='m-r-sm'>
					{statusToStyle[props.salesOrder.DeliveryStatus].image}
				</div>
				{DeliveryStatus.toString(props.salesOrder.DeliveryStatus)} {props.salesOrder.DeliveryPerson !== '' ? <span className='text-muted'> ({props.salesOrder.DeliveryPerson}) </span> : null}
			</div>
		</Col>
		<Col className='text-18 text-right font-bold'>
			<div className='text-right text-18 text-muted font-bold'>
				{props.salesOrder.PendingAmount === 0 ?
					<SvgIcon className='m-t-n-xs m-r-xs' color="#D6AF28" width={'16px'} height={'16px'} src={Images.TakeOutPaid} />
					: null}
				{props.salesOrder.IsInvoiced ?
					<SvgIcon className='m-t-n-xs m-r-xs' color="#75CA8D" width={'16px'} height={'16px'} src={Images.TakeOutInvoiced} />
					: null}
				{UI.Format.asMoney(props.salesOrder.NetAmount)}
			</div>
		</Col>
	</Cols>;
}

declare global {
	interface CommandType {
		searchTakeOutOrder: () => Promise<Result>
	}
}

Command.register('searchTakeOutOrder', Preconditions.ensureIsNotLite, async (): Promise<Result> => {
	const series = await SalesOrderFacade.getSeries();
	return Modal.show(SearchTakeOutOrderScreen, { existingSeries: series });
});